import {
	BusyIndicator,
	Button,
	DropOverMenu,
	IconButton,
	ValidationError
} from "client/Components";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	ensureNotNil,
	generateTrackingId,
	isNil,
	isNotNil,
	resolveFormattedTextString,
	getDataUrl
} from "client/Misc/utils";
import { clearFocus } from "client/Misc/domUtils";
import {
	ReactElement,
	RefObject,
	useCallback,
	useMemo,
	useRef,
	useState
} from "react";
import { BlobIdImageHubMediaComposerViewModel } from "client/Api/Hub/common.composer";
import { InlineActionList } from "../Builder/Actions/InlineActionList";
import { HubMediaSelectMenu } from "./HubMediaSelectMenu";
import { useEmbedInputDialog, EmbedInputDialogData } from "./EmbedInputDialog";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import styles from "./HubMediaSelect.module.less";
import {
	getHubImageUrl,
	HubItemImageRestrictionsModel,
	HubItemMediaKind
} from "client/Api/Hub/common";
import { HubMediaSelectLayout } from "./HubMediaSelectLayout";
import { getDisplayFriendlySize } from "client_legacy/Documents/fileExtensions";
import {
	AlertManager,
	DragOverEventArgs,
	DragOverEventResult,
	DropEventArgs,
	MediaQueryKind,
	useAlerts,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { FileSelectionEntry, SelectionEntry } from "client_legacy/FileUploader/utils";
import { partition } from "client_legacy/ndaw/arrayHelpers";
import { never } from "client_legacy/ndaw/errors";
import { localize } from "client_legacy/ndaw/culture";
import { useVideoInputDialog, VideoInputDialogData } from "./VideoInputDialog";
import { HubItemMediaFormModel } from "../Management/common";
import {
	Base64FileUriUploadWithMetadataAndAttributionModel,
	fileMetadataSymbol
} from "client/Misc/commonTypes";
import {
	FocalPointOptions,
	ImageFileWithFocalPoint,
	useFocalPointDialog
} from "../FocalPointDialog/FocalPointDialog";
import { DEFAULT_FOCAL_POINT } from "../FocalPointDialog/FocalPointEditorSection";
import { hubItemKindToHubDataSourceMap } from "../Sidebar/common";
import { IValueValidation } from "client/Validation/input";
import { SelectImageMenu } from "../Builder/Layout/Menus/SelectImageMenu";
import { formatList } from "client/Misc/cultureSpecificUtils";
import { DocumentLocationViewModel } from "client/Api/Hub/hubSettings";
import {
	HubImageSelectorKind,
	ImageWithAttribution,
	useHubImageSelector
} from "../Management/useHubImageSelector";
import { ImageAttributionSaveModel } from "client/Api/common";

export type HubMediaSelectProps = {
	hubItemKind: HubItemKind;
	attachable: IValueInputAttachable<HubItemMediaFormModel>;
	imageRestrictions: HubItemImageRestrictionsModel;
	defaultImageLocation: DocumentLocationViewModel | null;
	originalImageMedia: BlobIdImageHubMediaComposerViewModel | null;
	isDisabled: boolean;
	isPixabayImageGalleryAvailable: boolean;
	onDisableToolbar?(): void;
};

export const HubMediaSelect = (props: HubMediaSelectProps): ReactElement | null => {
	const {
		hubItemKind,
		attachable,
		imageRestrictions,
		defaultImageLocation,
		originalImageMedia,
		isDisabled,
		isPixabayImageGalleryAvailable,
		onDisableToolbar
	} = props;

	const [isFocused, setIsFocused] = useState<boolean>(false);
	const [selectMenuState, setSelectMenuState] = useState<SelectMenuState>({
		isOpen: false
	});

	const [isImageSelectMenuOpen, setIsImageSelectMenuOpen] = useState<boolean>(false);
	const [isBusy, setIsBusy] = useState<boolean>(false);

	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const alertManager: AlertManager = useAlerts();
	const contentRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const editActionsRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const hasMedia: boolean = attachable.value.mediaKind !== HubItemMediaKind.None;

	const [embedInputDialog, openEmbedInputDialog] = useEmbedInputDialog();
	const [videoInputDialog, openVideoInputDialog] = useVideoInputDialog();
	const [focalPointDialog, openFocalPointDialog] = useFocalPointDialog();

	const {
		maxImageSize,
		supportedFileExtensions: supportedImageExtensions,
		supportedMimeTypes: supportedImageMimeTypes
	} = imageRestrictions;

	const {
		dialogElement: selectImageDialog,
		onSelect: onSelectImage,
		onResolveFileForUpload
	} = useHubImageSelector({
		onIsBusyChange: setIsBusy,
		supportedImageExtensions,
		defaultImageLocation,
		alertManager,
		supportedMimeTypes: supportedImageMimeTypes,
		maxImageSize
	});

	const focalPointDialogOptions = useMemo(
		(): FocalPointOptions => ({
			maxImageSize: maxImageSize,
			supportedImageTypes: supportedImageMimeTypes,
			canSelectFromOnlineSources: true,
			supportedImageExtensions,
			defaultImageLocation,
			isPixabayImageGalleryAvailable
		}),
		[
			maxImageSize,
			supportedImageMimeTypes,
			supportedImageExtensions,
			defaultImageLocation,
			isPixabayImageGalleryAvailable
		]
	);

	const deleteMediaHandler = useCallback(
		(): void => {
			attachable.reducer
				.setValue({
					mediaKind: HubItemMediaKind.None
				});

			attachable.validation
				.triggerInputFinished();

			clearFocus();
		},
		[attachable]
	);

	const changeHandler = useCallback(
		(newValue: HubItemMediaFormModel): void => {
			attachable.reducer
				.setValue(newValue);

			// The setTimeout is used because when attachable.whenType is called the reducer isn't updated yet
			// and it's accessing the old value
			setTimeout(
				() => {
					attachable.whenType(
						x => x.mediaKind === HubItemMediaKind.Image,
						(image) => {
							const imageValidation: IValueValidation<OptionalModel<Base64FileUriUploadWithMetadataAndAttributionModel>> = image.for("image")
								.validation;

							imageValidation.clearServerErrors();
							imageValidation.triggerInputFinished();
						},
						(x) => {
							x.whenType(
								x => x.mediaKind === HubItemMediaKind.Video,
								(video) => {
									video.for("videoUrl")
										.validation
										.triggerInputFinished();
								},
								(x) => {
									x.whenType(
										x => x.mediaKind === HubItemMediaKind.Embed,
										(embed) => {
											embed.for("embedCode")
												.validation
												.triggerInputFinished();
										}
									);
								}
							);
						}
					);
				},
				1
			);

			attachable.validation
				.triggerInputFinished();

			contentRef
				.current
				?.focus();
		},
		[attachable]
	);

	const editFocalPointHandler = useCallback(
		async (): Promise<void> => {
			const { value } = attachable;

			if (value.mediaKind !== HubItemMediaKind.Image) {
				return;
			}

			const initialImageUrl: string | undefined = value.image
				.isSet
					? value.image
						.value
						.uri
					: isNotNil(originalImageMedia)
						? getHubImageUrl(
							hubItemKindToHubDataSourceMap[hubItemKind],
							originalImageMedia.imageId
						)
						: undefined;

			if (isNil(initialImageUrl)) {
				throw new Error("Cannot edit focal point without an image URL");
			}

			const result: TryResult<ImageFileWithFocalPoint> = await openFocalPointDialog({
				initialImageUrl,
				initialFocalPoint: value.focalPoint,
				options: focalPointDialogOptions,
				context: "editing-item"
			});

			if (!result.wasSuccessful) {
				return;
			}

			const {
				focalPoint,
				image
			} = result.result;

			changeHandler({
				mediaKind: HubItemMediaKind.Image,
				focalPoint: focalPoint,
				image: image.isSet
					? image
					: value.image
			});
		},
		[
			attachable,
			hubItemKind,
			originalImageMedia,
			focalPointDialogOptions,
			changeHandler,
			openFocalPointDialog,
		]
	);

	const imageSelectedHandler = useCallback(
		async (file: File, attribution: ImageAttributionSaveModel | null): Promise<void> => {
			try {
				const dataUrl: string = await getDataUrl(file);

				const media: HubItemMediaFormModel = {
					mediaKind: HubItemMediaKind.Image,
					image: {
						isSet: true,
						value: {
							uri: dataUrl,
							filename: file.name,
							[fileMetadataSymbol]: {
								size: file.size,
								mimeType: file.type
							},
							attribution
						}
					},
					focalPoint: DEFAULT_FOCAL_POINT
				};

				if (file.size > maxImageSize) {
					alertManager.showError(
						resolveFormattedTextString({
							template: localize("_MaxSizeIs"),
							replacements: [
								{
									what: "{0}",
									filler: getDisplayFriendlySize(maxImageSize)
								}
							]
						})
					);
				} else if (!supportedImageMimeTypes.includes(file.type)) {
					alertManager.showError(
						resolveFormattedTextString({
							template: localize("_SupportedImageFormats"),
							replacements: [
								{
									what: "{0}",
									filler: formatList([
										localize("_FileExtensionJpg"),
										localize("_FileExtensionPng"),
										localize("_FileExtensionGif")
									])
								}
							]
						})
					);
				} else {

					const result: TryResult<ImageFileWithFocalPoint> = await openFocalPointDialog({
						initialImageUrl: dataUrl,
						initialFocalPoint: DEFAULT_FOCAL_POINT,
						options: focalPointDialogOptions,
						context: "creating-item"
					});

					if (result.wasSuccessful) {
						const {
							focalPoint,
							image
						} = result.result;

						media.focalPoint = focalPoint;

						if (image.isSet) {
							media.image = image;
						}
					}
				}

				changeHandler(media);
			} catch (e) {
				console.error(e);
			}
		},
		[
			alertManager,
			maxImageSize,
			supportedImageMimeTypes,
			focalPointDialogOptions,
			changeHandler,
			openFocalPointDialog
		]
	);

	const selectImageHandler = useCallback(
		async (kind: HubImageSelectorKind): Promise<void> => {
			setIsImageSelectMenuOpen(false);

			const result: TryResult<ImageWithAttribution> = await onSelectImage(kind);

			if (!result.wasSuccessful) {
				return;
			}

			const {
				file,
				attribution
			} = result.result;

			imageSelectedHandler(file, attribution);
		},
		[imageSelectedHandler, onSelectImage]
	);

	const dropHandler = useCallback(
		async (args: DropEventArgs): Promise<void> => {
			const fileEntries: readonly SelectionEntry[] = await args.getFiles();

			const [directories, files] = partition(fileEntries, f => f.isDirectory);

			if (directories.length > 0) {
				alertManager.showError(localize("_YouCantUploadFolders"));
				return;
			}

			if (files.length === 0) {
				return;
			}

			const droppedFile: File = (files[0] as FileSelectionEntry).file;
			const file: File = await onResolveFileForUpload(droppedFile);

			imageSelectedHandler(file, null);
		},
		[
			alertManager,
			imageSelectedHandler,
			onResolveFileForUpload
		]
	);

	const dragOverHandler = useCallback(
		(args: DragOverEventArgs): DragOverEventResult => {
			const { types } = args;
			if (types.indexOf("Files") === -1) {
				return { isAccepted: false };
			}

			return {
				isAccepted: true,
				dropEffect: "copy"
			};
		},
		[]
	);

	const openEmbedInputDialogHandler = useCallback(
		(openData: EmbedInputDialogData): Promise<TryResult<string>> => {
			clearFocus();
			return openEmbedInputDialog(openData);
		},
		[openEmbedInputDialog]
	);

	const openVideoInputDialogHandler = useCallback(
		(openData: VideoInputDialogData): Promise<TryResult<string>> => {
			clearFocus();
			return openVideoInputDialog(openData);
		},
		[openVideoInputDialog]
	);

	const renderEditMenu = (target: HTMLElement): ReactElement => {
		return (
			<HubMediaSelectMenu
				value={attachable.value}
				onChange={changeHandler}
				onClose={() => setSelectMenuState({ isOpen: false })}
				target={target}
				onOpenEmbedInputDialog={openEmbedInputDialogHandler}
				onOpenVideoInputDialog={openVideoInputDialogHandler}
				onSelectImage={selectImageHandler}
				trackingId={generateTrackingId(attachable.reducer.path, "__media-select-menu")}
				isPixabayImageGalleryAvailable={isPixabayImageGalleryAvailable}
			/>
		);
	};

	const containerFocusHandler = (): void => {
		setIsFocused(true);
		onDisableToolbar?.();
	};

	const containerClickHandler = (): void => {
		if (!hasMedia) {
			setSelectMenuState({
				isOpen: true,
			});
		}
	};

	const editMediaHandler = useCallback(
		async (): Promise<void> => {
			setSelectMenuState({ isOpen: false });
			switch (attachable.value.mediaKind) {
				case HubItemMediaKind.Embed:
					const currentEmbedCode: string = attachable.value
						.embedCode;

					const embedResult: TryResult<string> = await openEmbedInputDialogHandler({
						code: currentEmbedCode
					});

					if (embedResult.wasSuccessful) {
						changeHandler({
							mediaKind: HubItemMediaKind.Embed,
							embedCode: embedResult.result
						});
					}
					break;
				case HubItemMediaKind.Video:
					const currentVideoUrl: string = attachable.value
						.videoUrl;

					const videoResult: TryResult<string> = await openVideoInputDialog({
						videoUrl: currentVideoUrl
					});

					if (videoResult.wasSuccessful) {
						changeHandler({
							mediaKind: HubItemMediaKind.Video,
							videoUrl: videoResult.result
						});
					}
					break;
				case HubItemMediaKind.Image:
					setIsImageSelectMenuOpen(true);
					break;
				case HubItemMediaKind.None:
					throw new Error("media must be image or video");
				default:
					never(attachable.value);
			}
		},
		[
			attachable.value,
			changeHandler,
			openEmbedInputDialogHandler,
			openVideoInputDialog
		]
	);

	const shouldRenderMediaEditActions: boolean = !isDisabled
		&& hasMedia
		&& isFocused;

	const shouldRenderSelectMenuForAdding: boolean = !isDisabled
		&& !hasMedia
		&& selectMenuState.isOpen;

	const shouldRenderEditFocalPoint: boolean = attachable.validation
		.isValid()
			&& attachable.value
				.mediaKind === HubItemMediaKind.Image
					&& (
						isNotNil(originalImageMedia)
							|| attachable.value
								.image
								.isSet
					);

	if (isDisabled && !hasMedia) {
		return null;
	}

	return (
		<>
			<HubMediaSelectLayout
				{...isBusy
					? { isReadonly: true }
					: {
						onClick: containerClickHandler,
						onFocus: containerFocusHandler,
						onBlur: () => {
							setIsFocused(false);
							setIsImageSelectMenuOpen(false);
						},
						onDragOver: dragOverHandler,
						onDrop: dropHandler
					}
				}
				ref={contentRef}
				trackingId={attachable.reducer.path}
			>
				{isBusy && <BusyIndicator />}
				<div className={styles.actionsDropOverAnchor}>
					{shouldRenderMediaEditActions && (
						<>
							<div
								ref={editActionsRef}
								className={styles.actionsDropOver}
							>
								<InlineActionList
									trackingId="media-actions"
								>
									<IconButton
										kind="transparent-colored"
										onClick={editMediaHandler}
										icon="pen"
										trackingId="edit-button"
									/>
									{isImageSelectMenuOpen && (
										<DropOverMenu
											target={ensureNotNil(editActionsRef.current)}
											onClose={() => setIsImageSelectMenuOpen(false)}
											title={localize("_SelectImage")}
											trackingId="select-image-dropover"
										>
											<SelectImageMenu
												trackingId="select-image-menu"
												onSelectImage={selectImageHandler}
												title={isResponsive ? undefined : localize("_SelectImage")}
												isPixabayImageGalleryAvailable={isPixabayImageGalleryAvailable}
											/>
										</DropOverMenu>
									)}
									<IconButton
										kind="transparent-colored"
										onClick={deleteMediaHandler}
										icon="trash-can"
										trackingId="delete-button"
									/>
									{shouldRenderEditFocalPoint && (
										<>
											<InlineActionList.Divider />
											<Button
												kind="transparent-for-list"
												onClick={editFocalPointHandler}
												icon="expand"
												trackingId="edit-focal-point"
												text={localize("_EditFocalPoint")}
											/>
										</>
									)}
								</InlineActionList>
							</div>
						</>
					)}
				</div>
				{attachable.whenType(
					x => x.mediaKind === HubItemMediaKind.None,
					_ => (
						<HubMediaSelectLayout.NoneMediaContent maxImageSize={maxImageSize} />
					),
					mediaAttachable => mediaAttachable.whenType(
						x => x.mediaKind === HubItemMediaKind.Image,
						imageAttachable => (
							<HubMediaSelectLayout.ImageMediaContent
								hubItemKind={hubItemKind}
								image={imageAttachable
									.value
									.image
								}
								originalImageMedia={originalImageMedia}
								hasError={!imageAttachable.validation.isValid()}
							/>
						),
						nonImageAttachable => (
							nonImageAttachable.whenType(
								x => x.mediaKind === HubItemMediaKind.Video,
								(videoAttachable) => (
									<HubMediaSelectLayout.VideoMediaContent
										videoUrl={videoAttachable
											.value
											.videoUrl
										}
									/>
								),
								(embedAttachable) => (
									<HubMediaSelectLayout.EmbedMediaContent
										embedCode={embedAttachable
											.value
											.embedCode
										}
									/>
								)
							)
						)
					)
				)}
			</HubMediaSelectLayout>
			{attachable.whenType(
				x => x.mediaKind === HubItemMediaKind.Image,
				imageAttachable => (
					<ValidationError
						{...imageAttachable.for("image")
							.attachToValidationError()}
					/>
				),
				nonImageAttachable => nonImageAttachable.whenType(
					x => x.mediaKind === HubItemMediaKind.Video,
					videoAttachable => (
						<ValidationError
							{...videoAttachable.for("videoUrl")
								.attachToValidationError()}
						/>
					),
					nonVideoAttachable => nonVideoAttachable.whenType(
						x => x.mediaKind === HubItemMediaKind.Embed,
						videoAttachable => (
							<ValidationError
								{...videoAttachable.for("embedCode")
									.attachToValidationError()}
							/>
						)
					)
				)
			)}
			{!hasMedia && shouldRenderSelectMenuForAdding && contentRef.current && (
				renderEditMenu(contentRef.current)
			)}
			{embedInputDialog}
			{videoInputDialog}
			{focalPointDialog}
			{selectImageDialog}
		</>
	);
};
HubMediaSelect.displayName = "HubMediaSelect";

type SelectMenuState = {
	isOpen: true;
} | {
	isOpen: false;
};